import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  Signal,
  viewChild,
  AfterViewInit,
  computed,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';

import { NoopValueAccessorDirective } from '@rp/shared/directives';
import { injectNgControl } from '@rp/utils';

import { RadioButton } from './models';
import { IconComponent } from '../icon';
import { TooltipDirective } from '../tooltip';

@Component({
  selector: 'rp-radio',
  standalone: true,
  templateUrl: './radio.component.html',
  host: {
    '[style.width]': '"100%"',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [NoopValueAccessorDirective],
  imports: [
    IconComponent,
    MatRadioButton,
    MatRadioGroup,
    NgClass,
    ReactiveFormsModule,
    TooltipDirective,
  ],
})
export class RadioComponent<T> implements AfterViewInit {
  radioGroupEl: Signal<ElementRef<HTMLElement>> = viewChild(MatRadioGroup, { read: ElementRef });

  /**
   * Radio buttons list
   */
  radioButtons = input.required<RadioButton<T>[]>();

  /**
   * Label position for radio buttons
   */
  labelPosition = input<'before' | 'after'>('after');

  /**
   * If radio buttons has disabled state
   */
  disabled = input(false, {
    transform: booleanAttribute,
  });

  /**
   * If radio buttons is required
   */
  required = input(false, {
    transform: booleanAttribute,
  });

  /**
   * Radio buttons group name, used to group radio buttons
   */
  groupName = input<string>();

  /**
   * Icon name for radio buttons
   */
  iconName = input<string>();

  /**
   * Icon tooltip message for icon
   */
  iconTooltipMessage = input<string>();

  /**
   * Show icon in tooltip or not
   */
  showIconInTooltip = input<boolean>();

  /**
   * If icon is clickable
   */
  isClickableIcon = input(false, {
    transform: booleanAttribute,
  });

  /**
   * If radio buttons has hidden count
   */
  showHiddenCount = input(false, {
    transform: booleanAttribute,
  });

  /**
   * Hidden count text
   */
  hiddenCountText = input<string>();

  /**
   * If radio buttons are shown in desktop mode
   */
  isDesktop = input<boolean>();

  showHiddenCountEnabled = computed(
    () => this.showHiddenCount() && this.hiddenCountText() && this.isDesktop(),
  );

  /**
   * If icon is clickable, iconClick should trigger value to outer container
   */
  iconClick = output<RadioButton<T>>();

  ngControl = injectNgControl();

  private readonly _renderer = inject(Renderer2);
  private readonly _elementRef = inject(ElementRef);

  constructor() {
    effect(() => {
      const { control } = this.ngControl;

      if (this.required()) {
        control.setValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }

      if (this.disabled()) {
        control.disable();
      } else {
        control.enable();
      }

      if (this.showHiddenCountEnabled()) {
        this._showHiddenCountAtLastItem();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.showHiddenCountEnabled()) {
      this._showHiddenCountAtLastItem();

      const resizeObserver = new ResizeObserver(() => {
        this._showHiddenCountAtLastItem();
      });

      resizeObserver.observe(this._elementRef.nativeElement);
    }
  }

  private _showHiddenCountAtLastItem(): void {
    const containerEl = this.radioGroupEl().nativeElement;
    const containerRect = containerEl.getBoundingClientRect();
    const children = Array.from(containerEl.children) as HTMLElement[];

    const tolerance = 2;
    let hiddenCount = 0;
    let lastVisibleItem: HTMLElement | null = null;

    children.forEach(child => {
      const itemRect = child.getBoundingClientRect();

      const verticallyVisible =
        itemRect.bottom > containerRect.top + tolerance &&
        itemRect.top < containerRect.bottom - tolerance;

      const horizontallyVisible =
        itemRect.right > containerRect.left + tolerance &&
        itemRect.left < containerRect.right - tolerance;

      const isVisible = verticallyVisible && horizontallyVisible;

      if (isVisible) {
        lastVisibleItem = child;
      } else {
        hiddenCount++;
      }
    });

    if (hiddenCount && lastVisibleItem) {
      const content = `+ ${hiddenCount} ${this.hiddenCountText()}`;

      if (content === lastVisibleItem.textContent) {
        return;
      }

      this._renderer.setProperty(lastVisibleItem, 'textContent', content);

      this._renderer.setStyle(lastVisibleItem, 'font-size', '12px');
      this._renderer.setStyle(lastVisibleItem, 'font-weight', '500');
      this._renderer.setStyle(lastVisibleItem, 'cursor', 'default');
    }
  }
}
